import { fromJS, Map, OrderedMap } from 'immutable'
import * as actionTypes from './constants'
import { LOGOUT, LOGIN } from '../authentication/constants'
import { PROJECT_CREATED } from '../CreateProject/constants'
import { PROJECT_DETAILS_SAVED } from '../ProjectSettings/constants'
import { sortProjectContent } from '../../utils/sortManager'
import { generateProjectPath } from '../../utils/generateProjectPath'
import safeIsNaN from '../../utils/safeIsNaN'

const initialState = fromJS({
  folderBatchSelection: {},
  projectBatchSelection: {},
  projectFolders: {},
  projectFolderTimestamps: {},
  projectsStateFilter: 'Active', // [Inactive, All]
  projectsSearch: '',
  projectsSort: 'Title' // [Name, Type]
})

function projectSelectionReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.LOAD_OWN_PROJECTS:
      return state
        .set('ownProjectsLoading', true)
        .delete('loadOwnProjectsError')
    case actionTypes.OWN_PROJECTS_LOADED: {
      return state
        .set('ownProjectsLoading', false)
        .delete('loadOwnProjectsError')
        .set('ownProjects', action.projects)
    }

    case actionTypes.SET_OWN_PROJECT_SORT:
      return state
        .set('ownProjectsSort', action.sort)
        .set('ownProjectsSortReverse', action.sortReverse)
    case actionTypes.PROJECTS_LOAD_ERROR:
      return state
        .set('ownProjectsLoading', false)
        .set('loadOwnProjectsError', action.error)
    case actionTypes.FOLDER_CREATED: {
      let newState = state

      let content
      const isInOpenFolder =
        action.folder.parentId &&
        newState.hasIn(['projectFolders', action.folder.parentId])
      const isInRootFolder = !action.folder.parentId

      if (isInOpenFolder) {
        content = newState.getIn([
          'projectFolders',
          action.folder.parentId,
          'content'
        ])
      } else if (isInRootFolder) {
        content = newState.getIn(['rootProjectsObject', 'content'])
      }

      if (content) {
        content = content.set(action.folder.id, fromJS(action.folder))
        content = content.sort(sortProjectContent(newState.get('projectsSort')))
      }

      if (isInOpenFolder) {
        newState = newState.setIn(
          ['projectFolders', action.folder.parentId, 'content'],
          content
        )
        newState = newState.setIn(
          ['projectFolders', action.folder.parentId, 'items'],
          newState.getIn(['projectFolders', action.folder.parentId, 'items']) +
            1
        )
      } else if (isInRootFolder) {
        newState = newState.setIn(['rootProjectsObject', 'content'], content)
      }

      return newState.set('folderCreated', true).set('createFolder', false)
    }
    case actionTypes.CREATE_FOLDER_ERROR:
      return state
        .set('createFolderError', action.error)
        .set('createFolder', false)
    case actionTypes.CLEAR_CREATE_FOLDER:
      return state
        .set('createFolderError', null)
        .set('folderCreated', false)
        .set('createFolder', false)
    case actionTypes.CREATE_FOLDER:
      return state.set('createFolder', true)
    case actionTypes.PROJECT_MOVED: {
      let newState = state
      let project

      // get folder out of old location an delete it there
      if (
        action.oldParentId &&
        newState.hasIn(['projectFolders', action.oldParentId])
      ) {
        project = newState.getIn([
          'projectFolders',
          action.oldParentId,
          'content',
          action.projectId
        ])
        newState = newState.deleteIn([
          'projectFolders',
          action.oldParentId,
          'content',
          action.projectId
        ])
        const parentIdOfOldParent = newState.getIn([
          'projectFolders',
          action.oldParentId,
          'parentId'
        ])
        if (
          parentIdOfOldParent === null ||
          parentIdOfOldParent ===
            newState.getIn(['rootProjectsObject', 'rootId'])
        ) {
          const newCount =
            newState.getIn([
              'rootProjectsObject',
              'content',
              action.oldParentId,
              'items'
            ]) - 1
          newState = newState.setIn(
            ['rootProjectsObject', 'content', action.oldParentId, 'items'],
            newCount
          )
        } else if (parentIdOfOldParent) {
          const newCount =
            newState.getIn([
              'projectFolders',
              parentIdOfOldParent,
              'content',
              action.oldParentId,
              'items'
            ]) - 1
          newState = newState.setIn(
            [
              'projectFolders',
              parentIdOfOldParent,
              'content',
              action.oldParentId,
              'items'
            ],
            newCount
          )
        }
      } else if (!action.oldParentId) {
        project = newState.getIn([
          'rootProjectsObject',
          'content',
          action.projectId
        ])
        newState = newState.deleteIn([
          'rootProjectsObject',
          'content',
          action.projectId
        ])
      }

      if (project) {
        project = project.set('parentId', action.parentId)
      }

      let content
      const isInOpenFolder =
        action.parentId && newState.hasIn(['projectFolders', action.parentId])
      const isInRootFolder = !action.parentId

      if (isInOpenFolder) {
        content = newState.getIn(['projectFolders', action.parentId, 'content'])
      } else if (isInRootFolder) {
        content = newState.getIn(['rootProjectsObject', 'content'])
      }

      if (content) {
        content = content.set(action.projectId, project)
        content = content.sort(sortProjectContent(state.get('projectsSort')))
      }

      if (isInRootFolder) {
        newState = newState.setIn(['rootProjectsObject', 'content'], content)
      } else {
        let parentIdOfParent

        if (isInOpenFolder) {
          newState = newState.setIn(
            ['projectFolders', action.parentId, 'content'],
            content
          )
          parentIdOfParent = newState.getIn([
            'projectFolders',
            action.parentId,
            'parentId'
          ])
        } else if (
          newState.hasIn(['rootProjectsObject', 'content', action.parentId])
        ) {
          parentIdOfParent = null
        } else {
          newState.get('projectFolders').forEach((projectFolder, folderId) => {
            projectFolder.get('content').forEach((item, itemId) => {
              if (itemId === action.parentId) {
                parentIdOfParent = folderId
              }
            })
          })
        }

        if (
          parentIdOfParent === null ||
          parentIdOfParent === newState.getIn(['rootProjectsObject', 'rootId'])
        ) {
          const newCount =
            newState.getIn([
              'rootProjectsObject',
              'content',
              action.parentId,
              'items'
            ]) + 1
          if (!safeIsNaN(newCount)) {
            newState = newState.setIn(
              ['rootProjectsObject', 'content', action.parentId, 'items'],
              newCount
            )
          }
        } else if (parentIdOfParent) {
          const newCount =
            newState.getIn([
              'projectFolders',
              parentIdOfParent,
              'content',
              action.parentId,
              'items'
            ]) + 1
          if (!safeIsNaN(newCount)) {
            newState = newState.setIn(
              [
                'projectFolders',
                parentIdOfParent,
                'content',
                action.parentId,
                'items'
              ],
              newCount
            )
          }
        }
      }

      return newState.set('projectMoved', true).set('moveProject', false)
    }
    case actionTypes.MOVE_PROJECT_ERROR:
      return state
        .set('moveProjectError', action.error)
        .set('moveProject', false)
    case actionTypes.CLEAR_MOVE_PROJECT:
      return state
        .set('moveProjectError', null)
        .set('projectMoved', false)
        .set('moveProject', false)
    case actionTypes.MOVE_PROJECT:
      return state.set('moveProject', true)
    case actionTypes.FOLDER_MOVED: {
      let newState = state
      let folder

      // get folder out of old location an delete it there
      if (
        action.oldParentId &&
        newState.hasIn(['projectFolders', action.oldParentId])
      ) {
        folder = newState.getIn([
          'projectFolders',
          action.oldParentId,
          'content',
          action.folderId
        ])
        newState = newState.deleteIn([
          'projectFolders',
          action.oldParentId,
          'content',
          action.folderId
        ])
        const parentIdOfOldParent = newState.getIn([
          'projectFolders',
          action.oldParentId,
          'parentId'
        ])
        if (
          parentIdOfOldParent === null ||
          parentIdOfOldParent ===
            newState.getIn(['rootProjectsObject', 'rootId'])
        ) {
          const newItemsCount =
            newState.getIn([
              'rootProjectsObject',
              'content',
              action.oldParentId,
              'items'
            ]) - 1

          newState = newState.setIn(
            ['rootProjectsObject', 'content', action.oldParentId, 'items'],
            newItemsCount
          )
        } else if (parentIdOfOldParent) {
          const newItemsCount =
            newState.getIn([
              'projectFolders',
              parentIdOfOldParent,
              'content',
              action.oldParentId,
              'items'
            ]) - 1
          newState = newState.setIn(
            [
              'projectFolders',
              parentIdOfOldParent,
              'content',
              action.oldParentId,
              'items'
            ],
            newItemsCount
          )
        }
      } else if (!action.oldParentId) {
        folder = newState.getIn([
          'rootProjectsObject',
          'content',
          action.folderId
        ])
        newState = newState.deleteIn([
          'rootProjectsObject',
          'content',
          action.folderId
        ])
      }

      if (folder) {
        folder = folder.set('parentId', action.parentId)
      }

      let content
      const isInOpenFolder =
        action.parentId && newState.hasIn(['projectFolders', action.parentId])
      const isInRootFolder = !action.parentId

      if (isInOpenFolder) {
        content = newState.getIn(['projectFolders', action.parentId, 'content'])
      } else if (isInRootFolder) {
        content = newState.getIn(['rootProjectsObject', 'content'])
      }

      if (content) {
        content = content.set(action.folderId, folder)
        content = content.sort(sortProjectContent(newState.get('projectsSort')))
      }

      if (isInRootFolder) {
        newState = newState.setIn(['rootProjectsObject', 'content'], content)
      } else {
        let parentIdOfParent

        if (isInOpenFolder) {
          newState = newState.setIn(
            ['projectFolders', action.parentId, 'content'],
            content
          )
          parentIdOfParent = newState.getIn([
            'projectFolders',
            action.parentId,
            'parentId'
          ])
        } else if (
          newState.hasIn(['rootProjectsObject', 'content', action.parentId])
        ) {
          parentIdOfParent = null
        } else {
          newState.get('projectFolders').forEach((projectFolder, folderId) => {
            projectFolder.get('content').forEach((item, itemId) => {
              if (itemId === action.parentId) {
                parentIdOfParent = folderId
              }
            })
          })
        }

        if (
          parentIdOfParent === null ||
          parentIdOfParent === newState.getIn(['rootProjectsObject', 'rootId'])
        ) {
          const newCount =
            newState.getIn([
              'rootProjectsObject',
              'content',
              action.parentId,
              'items'
            ]) + 1
          if (!safeIsNaN(newCount)) {
            newState = newState.setIn(
              ['rootProjectsObject', 'content', action.parentId, 'items'],
              newCount
            )
          }
        } else if (parentIdOfParent) {
          const newCount =
            newState.getIn([
              'projectFolders',
              parentIdOfParent,
              'content',
              action.parentId,
              'items'
            ]) + 1
          if (!safeIsNaN(newCount)) {
            newState = newState.setIn(
              [
                'projectFolders',
                parentIdOfParent,
                'content',
                action.parentId,
                'items'
              ],
              newCount
            )
          }
        }
      }

      return newState.set('folderMoved', true).set('moveFolder', false)
    }
    case actionTypes.MOVE_FOLDER_ERROR:
      return state.set('moveFolderError', action.error).set('moveFolder', false)
    case actionTypes.CLEAR_MOVE_FOLDER:
      return state
        .set('moveFolderError', null)
        .set('folderMoved', false)
        .set('moveFolder', false)
    case actionTypes.MOVE_FOLDER:
      return state.set('moveFolder', true)
    case actionTypes.SELECT_FOLDER_BATCH:
      return state.setIn(
        ['folderBatchSelection', action.folder.id],
        action.folder
      )
    case actionTypes.MOVE_ITEMS_BATCH:
      return state.set('moveItemsBatchLoading', true)
    case actionTypes.ITEMS_BATCH_MOVED:
      return state
        .set('itemsBatchMoved', true)
        .set('moveItemsBatchLoading', false)
        .set('folderBatchSelection', Map())
        .set('projectBatchSelection', Map())
    case actionTypes.MOVE_ITEMS_BATCH_ERROR:
      return state
        .set('moveItemsBatchError', action.error)
        .set('itemsBatchMoved', false)
        .set('moveItemsBatchLoading', false)
    case actionTypes.CLEAR_MOVE_ITEMS_BATCH:
      return state
        .set('moveItemsBatchError', null)
        .set('itemsBatchMoved', false)
        .set('moveItemsBatchLoading', false)
    case actionTypes.DESELECT_FOLDER_BATCH:
      return state.deleteIn(['folderBatchSelection', action.folder.id])
    case actionTypes.DESELECT_ALL_FOLDER_BATCH:
      return state.set('folderBatchSelection', fromJS({}))
    case actionTypes.SELECT_PROJECT_BATCH:
      return state.setIn(
        ['projectBatchSelection', action.project.id],
        action.project
      )
    case actionTypes.DESELECT_PROJECT_BATCH:
      return state.deleteIn(['projectBatchSelection', action.project.id])
    case actionTypes.DESELECT_ALL_PROJECT_BATCH:
      return state.set('projectBatchSelection', fromJS({}))

    case actionTypes.UPDATE_FOLDER:
      return state.set('updatingFolder', true)
    case actionTypes.UPDATE_FOLDER_DONE: {
      let newState = state

      if (action.parentId !== undefined) {
        if (
          action.parentId &&
          newState.hasIn(['projectFolders', action.parentId])
        ) {
          newState = newState.mergeIn(
            ['projectFolders', action.parentId, 'content', action.folderId],
            fromJS(action.options)
          )
        } else if (!action.parentId) {
          newState = newState.mergeIn(
            ['rootProjectsObject', 'content', action.folderId],
            fromJS(action.options)
          )
        }
      }

      return newState.set('updatingFolder', false)
    }
    case actionTypes.UPDATE_FOLDER_ERROR:
      return state
        .set('updateFolderError', action.error)
        .set('updatingFolder', false)

    case actionTypes.UPDATE_PROJECT:
      return state.delete('updateProjectError').set('updatingProject', true)
    case actionTypes.UPDATE_PROJECT_ERROR:
      return state
        .set('updateProjectError', action.error)
        .set('updatingProject', false)
    case actionTypes.UPDATE_PROJECT_DONE: {
      let newState = state

      const stateFilter = state.get('projectsStateFilter')
      if (
        (stateFilter === 'Active' && !action.project.active) ||
        (stateFilter === 'Inactive' && action.project.active)
      ) {
        if (action.parentId) {
          newState = newState
            .deleteIn([
              'projectFolders',
              action.parentId,
              'content',
              action.project.id
            ])
            .setIn(
              ['projectFolders', action.parentId, 'items'],
              newState.getIn(['projectFolders', action.parentId, 'items']) - 1
            )
        } else {
          newState = newState
            .deleteIn(['rootProjectsObject', 'content', action.project.id])
            .setIn(
              ['rootProjectsObject', 'items'],
              newState.getIn(['rootProjectsObject', 'items']) - 1
            )
        }

        if (stateFilter === 'Active' && !action.project.active) {
          newState = newState.set(
            'ownProjects',
            (state.get('ownProjects') || []).filter(
              (p) => p.id !== action.project.id
            )
          )
        }
      } else {
        const project = {
          id: action.project.id,
          name: action.project.name,
          parentId: action.project.parentId,
          type: 'Project',
          active: action.project.active,
          lastActivity: action.project.lastModified,
          items: 0,
          permissions: action.project.permissions
        }

        if (action.parentId) {
          newState = newState.setIn(
            ['projectFolders', action.parentId, 'content', action.project.id],
            fromJS(project)
          )
        } else {
          project.parentId = null
          newState = newState.setIn(
            ['rootProjectsObject', 'content', action.project.id],
            fromJS(project)
          )
        }

        const currentOwnProject = state.get('ownProjects') || [project]
        newState = newState.set(
          'ownProjects',
          currentOwnProject.map((p) => {
            if (p.id === action.project.id) {
              return {
                ...p,
                ...project
              }
            }
            return p
          })
        )
      }

      return newState.set('updatingProject', false)
    }

    case actionTypes.UPDATE_PROJECT_BATCH_DONE: {
      let newState = state
      const stateFilter = newState.get('projectsStateFilter')
      if (
        (stateFilter === 'Active' && !action.options.active) ||
        (stateFilter === 'Inactive' && action.options.active)
      ) {
        newState.get('projectBatchSelection').forEach(({ id, parentId }) => {
          if (parentId) {
            newState = newState
              .deleteIn(['projectFolders', parentId, 'content', id])
              .setIn(
                ['projectFolders', parentId, 'items'],
                newState.getIn(['projectFolders', parentId, 'items']) - 1
              )
          } else {
            newState = newState
              .deleteIn(['rootProjectsObject', 'content', id])
              .setIn(
                ['rootProjectsObject', 'items'],
                newState.getIn(['rootProjectsObject', 'items']) - 1
              )
          }

          if (stateFilter === 'Active' && !action.options.active) {
            newState = newState.set(
              'ownProjects',
              (newState.get('ownProjects') || []).filter((p) => p.id !== id)
            )
          }
        })
      } else {
        newState.get('projectBatchSelection').forEach(({ id, parentId }) => {
          if (parentId) {
            newState = newState.mergeIn(
              ['projectFolders', parentId, 'content', id],
              fromJS(action.options)
            )
          } else {
            newState = newState.mergeIn(
              ['rootProjectsObject', 'content', id],
              fromJS(action.options)
            )
          }

          newState = newState.set(
            'ownProjects',
            state.get('ownProjects').map((p) => {
              if (p.id === id) {
                return {
                  ...p,
                  ...action.options
                }
              }
              return p
            })
          )
        })
      }

      return newState
        .set('projectBatchSelection', Map())
        .set('folderBatchSelection', Map())
    }
    case actionTypes.UPDATE_PROJECT_BATCH_ERROR:
      return state.set('updateProjectBatchError', action.error)

    case actionTypes.CHECK_CAN_CREATE_PROJECT:
      return state
        .delete('canCreateProject')
        .delete('checkCanCreateProjectError')
    case actionTypes.CHECK_CAN_CREATE_PROJECT_DONE:
      return state.set('canCreateProject', action.canCreateProject)
    case actionTypes.CHECK_CAN_CREATE_PROJECT_ERROR:
      return state.set('checkCanCreateProjectError', action.error)

    case actionTypes.USER_LEAVE_PROJECT:
      return state
        .set('userLeavingProject', true)
        .delete('userLeaveProjectError')
    case actionTypes.USER_LEAVE_PROJECT_DONE: {
      const ownProjects = state.get('ownProjects')

      return state.set('userLeavingProject', false).set(
        'ownProjects',
        (ownProjects || []).filter((p) => p.id !== action.projectId)
      )
    }
    case actionTypes.USER_LEAVE_PROJECT_ERROR:
      return state
        .set('userLeaveProjectError', action.error)
        .set('userLeavingProject', false)

    case actionTypes.DELETE_EMPTY_FOLDER:
      return state
        .set('deletingEmptyFolder', true)
        .delete('deleteEmptyFolderError')
    case actionTypes.DELETE_EMPTY_FOLDER_DONE: {
      let newState = state
      if (action.parentId) {
        newState = newState
          .deleteIn([
            'projectFolders',
            action.parentId,
            'content',
            action.folderId
          ])
          .setIn(
            ['projectFolders', action.parentId, 'items'],
            newState.getIn(['projectFolders', action.parentId, 'items']) - 1
          )
      } else {
        newState = newState
          .deleteIn(['rootProjectsObject', 'content', action.folderId])
          .setIn(
            ['rootProjectsObject', 'items'],
            newState.getIn(['rootProjectsObject', 'items']) - 1
          )
      }

      return newState.set('deletingEmptyFolder', false)
    }
    case actionTypes.DELETE_EMPTY_FOLDER_ERROR:
      return state
        .set('deleteEmptyFolderError', action.error)
        .set('deletingEmptyFolder', false)

    case actionTypes.CLEAR_PROJECTS:
      return state
        .delete('rootProjectsObject')
        .set('projectFolders', fromJS({}))
    case actionTypes.GET_ROOT_PROJECTS:
      return state
        .set('gettingRootProjects', true)
        .set('projectsSort', action.sort || state.get('projectsSort'))
        .set(
          'projectsStateFilter',
          action.stateFilter || state.get('projectsStateFilter')
        )
        .set(
          'projectsSearch',
          typeof action.search === 'string'
            ? action.search
            : state.get('projectsSearch')
        )
        .delete('getRootProjectsError')
    case actionTypes.GET_ROOT_PROJECTS_DONE: {
      let rootProjectsObject = state.has('rootProjectsObject')
        ? state.get('rootProjectsObject')
        : fromJS({
            totalPages: 0,
            totalElements: 0,
            number: 0,
            rootId: null
          })

      // set page options
      rootProjectsObject = rootProjectsObject
        .set('totalPages', action.projects.totalPages)
        .set('totalElements', action.projects.totalElements)
        .set('number', action.projects.number)
        .set('rootId', action.projects.id)

      // add content to content, id is already there -> merge it
      let content = state.has('rootProjectsObject')
        ? state.getIn(['rootProjectsObject', 'content'])
        : new OrderedMap()
      action.projects.content.forEach((element) => {
        content = content.set(element.id, fromJS(element))
      })

      return state
        .set('gettingRootProjects', action.cached)
        .set('rootProjectsObject', rootProjectsObject.set('content', content))
        .delete('getProjectTreeCancelToken')
        .set('rootProjectsTimestamp', action.timestamp)
    }
    case actionTypes.GET_ROOT_PROJECTS_ERROR:
      return state
        .set('gettingRootProjects', false)
        .set('getRootProjectsError', action.error)

    // is called when there is still content to load
    case actionTypes.GET_FOLDER_PROJECTS: {
      let newState = state

      if (newState.hasIn(['projectFolders', action.folderId])) {
        newState = newState.setIn(
          ['projectFolders', action.folderId, 'open'],
          true
        )
      } else {
        const folder = fromJS({
          totalPages: 0,
          totalElements: 0,
          number: 0,
          open: true
        })

        newState = newState.setIn(
          ['projectFolders', action.folderId],
          folder.set('content', new OrderedMap())
        )
      }

      return newState
        .set('gettingFolderProjects', action.folderId)
        .delete(`getFolderProjectsError${action.folderId}`)
    }
    case actionTypes.GET_FOLDER_PROJECTS_DONE: {
      // object is already there because it's created on GET_FOLDER_PROJECTS
      let folderProjectsObject = state.getIn([
        'projectFolders',
        action.folderId
      ])

      // set page options
      folderProjectsObject = folderProjectsObject
        .set('totalPages', action.projects.totalPages)
        .set('totalElements', action.projects.totalElements)
        .set('number', action.projects.number)
        .set(
          'parentId',
          action.projects.parentId ===
            state.getIn(['rootProjectsObject', 'rootId'])
            ? null
            : action.projects.parentId
        )

      // add content to content, id is already there -> merge it
      let content = state.hasIn(['projectFolders', action.folderId])
        ? state.getIn(['projectFolders', action.folderId, 'content'])
        : new OrderedMap()
      action.projects.content.forEach((element) => {
        content = content.set(element.id, fromJS(element))

        // add the open attribute to the folder
        if (element.type === 'Folder') {
          content = content.setIn([element.id, 'open'], false)
        }
      })

      return state
        .set('gettingFolderProjects', action.cached)
        .setIn(
          ['projectFolders', action.folderId],
          folderProjectsObject.set('content', content)
        )
        .setIn(['projectFolderTimestamps', action.folderId], action.timestamp)
        .delete('getProjectTreeCancelToken')
    }
    case actionTypes.GET_FOLDER_PROJECTS_ERROR:
      return state
        .set('gettingFolderProjects', false)
        .set(`getFolderProjectsError${action.folderId}`, action.error)
    case actionTypes.OPEN_PROJECT_FOLDER:
      return state.setIn(['projectFolders', action.folderId, 'open'], true)
    case actionTypes.CLOSE_PROJECT_FOLDER:
      return state.setIn(['projectFolders', action.folderId, 'open'], false)
    case actionTypes.SET_GET_PROJECT_TREE_CANCEL_TOKEN:
      return state.set('getProjectTreeCancelToken', action.cancelToken)

    case PROJECT_CREATED: {
      let newState = state
      if (action.role) {
        const ownProjects = newState.get('ownProjects')
        newState = state.set(
          'ownProjects',
          ownProjects && ownProjects.length
            ? [...ownProjects, action.project]
            : [action.project]
        )
      }

      let content
      let projectIdToUpdate = action.project.id
      let isInClosedSubFolderOfOpenFolder = false

      const isInOpenFolder =
        action.project.parentId &&
        newState.hasIn(['projectFolders', action.project.parentId])

      const isInClosedRootSubFolder =
        action.project.parentId &&
        !newState.hasIn(['projectFolders', action.project.parentId]) &&
        newState.hasIn([
          'rootProjectsObject',
          'content',
          action.project.parentId
        ])

      const isInRootFolder =
        !action.project.parentId ||
        newState.getIn(['rootProjectsObject', 'rootId']) ===
          action.project.parentId

      if (isInOpenFolder) {
        content = newState.getIn([
          'projectFolders',
          action.project.parentId,
          'content'
        ])
      } else if (isInRootFolder || isInClosedRootSubFolder) {
        content = newState.getIn(['rootProjectsObject', 'content'])
      } else {
        newState.get('projectFolders').forEach((projectFolder, id) => {
          const parentFolder = projectFolder.getIn([
            'content',
            action.project.parentId
          ])
          if (parentFolder && !parentFolder.isEmpty()) {
            projectIdToUpdate = id
            isInClosedSubFolderOfOpenFolder = true
            const updatedProjectFolder = projectFolder.setIn(
              ['content', action.project.parentId, 'items'],
              parentFolder.get('items') + 1
            )
            content = updatedProjectFolder.get('content')
          }
        })
      }

      if (content) {
        if (isInClosedRootSubFolder) {
          content = content.setIn(
            [action.project.parentId, 'items'],
            content.getIn([action.project.parentId, 'items']) + 1
          )
        } else if (!isInClosedSubFolderOfOpenFolder) {
          content = content.set(projectIdToUpdate, fromJS(action.project))
          content = content.sort(
            sortProjectContent(newState.get('projectsSort'))
          )
        }
      }

      if (isInOpenFolder) {
        return newState
          .setIn(
            ['projectFolders', action.project.parentId, 'content'],
            content
          )
          .setIn(
            ['projectFolders', action.project.parentId, 'items'],
            newState.getIn([
              'projectFolders',
              action.project.parentId,
              'items'
            ]) + 1
          )
      }
      if (isInClosedSubFolderOfOpenFolder) {
        return newState.setIn(
          ['projectFolders', projectIdToUpdate, 'content'],
          content
        )
      }
      if (isInRootFolder || isInClosedRootSubFolder) {
        return newState.setIn(['rootProjectsObject', 'content'], content)
      }

      return newState
    }

    case PROJECT_DETAILS_SAVED: {
      let newState = state

      if (action.fields.name) {
        newState = newState.set(
          'ownProjects',
          state.get('ownProjects').map((p) => {
            if (p.id === action.projectId) {
              return {
                ...p,
                name: action.fields.name
              }
            }
            return p
          })
        )

        newState = newState
          .setIn(
            [
              'projectFolders',
              action.parentId,
              'content',
              action.projectId,
              'name'
            ],
            action.fields.name
          )
          .setIn(
            ['rootProjectsObject', 'content', action.projectId, 'name'],
            action.fields.name
          )
      }
      return newState
    }

    case actionTypes.GET_PROJECT_PATH_DONE: {
      const path = generateProjectPath(action.projectFolder)
      return state.setIn(
        ['rootProjectsObject', 'content', action.projectId, 'path'],
        path
      )
    }
    case actionTypes.GET_PROJECT_PATH_ERROR: {
      return state.setIn(
        ['rootProjectsObject', 'content', action.projectId, 'path'],
        actionTypes.FAILED
      )
    }

    case LOGIN:
    case LOGOUT:
      return initialState
    default:
      return state
  }
}

export default projectSelectionReducer
